import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuItem,
  Select,
  FormControl,
  Button,
  Box,
  Typography,
  Modal,
  TextField,
  Paper,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Skeleton
} from "@mui/material";
import { useSelector } from "react-redux";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SnackbarComponent from "./common/Snackbar";
import axiosInstance from "../Api/Instance";
import { CandidateContext } from "./Context";
import { API_URI, END_POINTS, ALERTS } from "./Constants";
import { datetimeCoverter } from "./common/UsefulComponents";

const UserHomePage = () => {
  const [sharedCandidates, setSharedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [newRemarks, setNewRemarks] = useState("");
  const [candidateId, setCandidateId] = useState("");
  const [shareId, setShareId] = useState("");
  const [editedRows, setEditedRows] = useState(new Set());
  const [message, setMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [orderBy, setOrderBy] = useState("sharedAt");
  const [order, setOrder] = useState("desc");

  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const userId = user?._id;
  const { CANDIDATE } = END_POINTS;

  useEffect(() => {
    const fetchSharedCandidates = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/get-shared-candidates/${userId}`);
        setSharedCandidates(response.data.data || []);
      } catch (error) {
        console.error("Error fetching candidates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSharedCandidates();
  }, [userId]);

  const handleViewProfile = useCallback(
    async (candidateId) => {
      try {
        const response = await axiosInstance.get(`${CANDIDATE}/${candidateId}`);
        navigate(`${CANDIDATE}/${candidateId}`, {
          state: { candidate: response.data }
        });
      } catch (error) {
        console.error(`${ALERTS.HTTP_ERROR} :`, error.response?.status || error.message);
      }
    },
    [CANDIDATE, navigate]
  );

  const handleCandidateStatusChange = useCallback((candidate_id, share_id, status) => {
    setCandidateId(candidate_id);
    setShareId(share_id);
    setNewStatus(status);
    setOpen(true);
  }, []);

  const handleRemarksChange = useCallback((candidateId, shareId, remarks) => {
    setSharedCandidates((prev) =>
      prev.map((candidate) =>
        candidate.candidateId._id === candidateId && candidate._id === shareId
          ? { ...candidate, remarks }
          : candidate
      )
    );

    setEditedRows((prev) => {
      const newEditedRows = new Set(prev);
      newEditedRows.add(candidateId);
      return newEditedRows;
    });
  }, []);

  const handleStatusChange = useCallback(async () => {
    try {
      const response = await axiosInstance.post(
        `${END_POINTS.UPDATE_STATUS_CANDIDATE}`,
        { shareId, status: newStatus, remarks: newRemarks },
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );
      if (response.status === 200) {
        setSharedCandidates((prev) =>
          prev.map((candidate) =>
            candidate.candidateId._id === candidateId
              ? { ...candidate, status: newStatus }
              : candidate
          )
        );
        setOpen(false);
        setMessage("Candidate status changed successfully");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Error updating candidate status:", error);
    }
  }, [candidateId, newStatus, newRemarks, shareId]);

  const handleSaveRemarks = useCallback(
    async (shareId, remarks) => {
      try {
        const response = await axiosInstance.post(
          `${END_POINTS.SAVE_REMARKS}`,
          { shareId, remarks },
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        if (response.status === 200) {
          setSharedCandidates((prevCandidates) =>
            prevCandidates.map((candidate) =>
              candidate._id === shareId ? { ...candidate, remarks: remarks } : candidate
            )
          );
          setMessage("Candidate remarks changed successfully");
          setOpenSnackbar(true);

          setEditedRows((prev) => {
            const newEditedRows = new Set(prev);
            newEditedRows.delete(shareId);
            return newEditedRows;
          });
        }
      } catch (error) {
        console.error("Error saving remarks:", error);
        alert("An error occurred while saving remarks.");
      }
    },
    []
  );

  const handleSnackbarClose = useCallback(() => setOpenSnackbar(false), []);
  const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);
  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);
  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const getStatusChipColor = useCallback((status) => {
    switch (status) {
      case "Pending":
        return { bg: "#FEF3C7", text: "#92400E", border: "#F59E0B" };
      case "Reviewed":
        return { bg: "#D1FAE5", text: "#065F46", border: "#10B981" };
      case "On Hold":
        return { bg: "#FECACA", text: "#991B1B", border: "#EF4444" };
      case "Shared":
        return { bg: "#DBEAFE", text: "#1E40AF", border: "#3B82F6" };
      default:
        return { bg: "#F3F4F6", text: "#374151", border: "#9CA3AF" };
    }
  }, []);

  const sortedData = useMemo(() => {
    const comparator = (a, b) => {
      if (orderBy === "sharedAt") {
        return order === "desc"
          ? new Date(b.sharedAt) - new Date(a.sharedAt)
          : new Date(a.sharedAt) - new Date(b.sharedAt);
      }
      if (orderBy === "name") {
        return order === "desc"
          ? b.candidateId.name.localeCompare(a.candidateId.name)
          : a.candidateId.name.localeCompare(b.candidateId.name);
      }
      if (orderBy === "status") {
        return order === "desc"
          ? b.status.localeCompare(a.status)
          : a.status.localeCompare(b.status);
      }
      return 0;
    };
    return [...sharedCandidates].sort(comparator);
  }, [sharedCandidates, order, orderBy]);

  const paginatedData = useMemo(
    () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedData, page, rowsPerPage]
  );

  return (
 <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 w-full">
  <div className="px-4 md:px-8 py-6 w-[100%]">
        <SnackbarComponent
          open={openSnackbar}
          handleClose={handleSnackbarClose}
          message={message}
        />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
            <Typography
              variant="h4"
              className="font-bold bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent"
            >
              Candidate Review Dashboard
            </Typography>
          </div>
          <Typography variant="body1" className="text-gray-600 ml-4">
            Review candidates shared with you by other team members
          </Typography>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {loading
            ? Array(4)
              .fill()
              .map((_, i) => (
                <Card key={i} className="shadow-lg border-0">
                  <CardContent className="p-4">
                    <Skeleton variant="rectangular" height={60} className="rounded-lg mb-3" />
                    <Skeleton width="60%" />
                  </CardContent>
                </Card>
              ))
            : [
              { label: "Total Candidates", value: sharedCandidates?.length || 0, color: "from-blue-500 to-blue-600" },
              { label: "Pending Review", value: sharedCandidates?.filter(c => c.status === "Pending").length || 0, color: "from-yellow-500 to-yellow-600" },
              { label: "Reviewed", value: sharedCandidates?.filter(c => c.status === "Reviewed").length || 0, color: "from-green-500 to-green-600" },
              { label: "On Hold", value: sharedCandidates?.filter(c => c.status === "On Hold").length || 0, color: "from-red-500 to-red-600" }
            ].map((stat, index) => (
              <Card key={index} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
                <CardContent className="p-4">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${stat.color} flex items-center justify-center mb-3`}>
                    <Typography variant="h6" className="text-white font-bold">
                      {stat.value}
                    </Typography>
                  </div>
                  <Typography variant="body2" className="text-gray-600 font-medium">
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            ))}
        </div>

        {/* Modal */}
        <Modal open={open} onClose={() => setOpen(false)}>
          <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md mx-4">
            <Card className="shadow-2xl border-0">
              <CardContent className="p-6">
                <Typography variant="h6" className="text-gray-800 font-semibold mb-4 text-center">
                  Confirm Status Change
                </Typography>
                <Typography className="text-gray-600 text-center mb-6">
                  Are you sure you want to change the candidate status to <strong>{newStatus}</strong>?
                </Typography>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button variant="contained" onClick={handleStatusChange} className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800">
                    Confirm Change
                  </Button>
                  <Button variant="outlined" onClick={() => setOpen(false)} className="border-gray-300 text-gray-600 hover:border-gray-400">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Box>
        </Modal>

        {/* Table */}
        <Card className="shadow-lg border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <EditIcon className="text-gray-600" />
              <Typography variant="h6" className="font-semibold text-gray-800">
                Candidate Reviews
              </Typography>
            </div>

            <TableContainer component={Paper} className="border border-gray-200 rounded-lg">
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow className="bg-gray-50">
                    <TableCell className="font-semibold text-gray-700">
                      <TableSortLabel
                        active={orderBy === "name"}
                        direction={orderBy === "name" ? order : "asc"}
                        onClick={() => handleRequestSort("name")}
                      >
                        Candidate Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className="font-semibold text-gray-700">Shared By</TableCell>
                    <TableCell className="font-semibold text-gray-700">
                      <TableSortLabel
                        active={orderBy === "status"}
                        direction={orderBy === "status" ? order : "asc"}
                        onClick={() => handleRequestSort("status")}
                      >
                        Review Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className="font-semibold text-gray-700">Remarks</TableCell>
                    <TableCell className="font-semibold text-gray-700">Actions</TableCell>
                    <TableCell className="font-semibold text-gray-700">
                      <TableSortLabel
                        active={orderBy === "sharedAt"}
                        direction={orderBy === "sharedAt" ? order : "asc"}
                        onClick={() => handleRequestSort("sharedAt")}
                      >
                        Shared At
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array(rowsPerPage)
                      .fill()
                      .map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={6}>
                            <Skeleton height={40} />
                          </TableCell>
                        </TableRow>
                      ))
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography className="text-gray-600 py-8">
                          No candidates for review
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((share, index) => {
                      const colors = getStatusChipColor(share.status);
                      return (
                        <TableRow
                          key={share._id}
                          className={`hover:bg-gray-50 ${index % 2 === 0 ? "bg-white" : "bg-gray-25"}`}
                          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                        >
                          <TableCell>
                            <Box
                              className="flex items-center gap-2 cursor-pointer"
                              onClick={() => handleViewProfile(share.candidateId._id)}
                            >
                              <PersonIcon className="text-blue-600 text-lg" />
                              <Typography
                                className="text-blue-700 hover:text-blue-900 font-semibold hover:underline transition-colors"
                                sx={{ fontSize: 14 }}
                              >
                                {share.candidateId.name}
                              </Typography>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Typography className="text-gray-700 font-medium" sx={{ fontSize: 14 }}>
                              {share.sharedBy.name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={share.status}
                              size="small"
                              sx={{
                                backgroundColor: colors.bg,
                                color: colors.text,
                                border: `1px solid ${colors.border}`,
                                fontWeight: 600,
                                fontSize: 12,
                                height: 24
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              value={share.remarks || ""}
                              onChange={(e) =>
                                handleRemarksChange(share.candidateId._id, share._id, e.target.value)
                              }
                              placeholder="Enter remarks..."
                              size="small"
                              fullWidth
                              variant="outlined"
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontSize: 13,
                                  backgroundColor: "#FAFAFA",
                                  "&:hover": { backgroundColor: "#FFFFFF" },
                                  "&.Mui-focused": { backgroundColor: "#FFFFFF" }
                                },
                                maxWidth: 200
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Box className="flex items-center gap-2">
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  value={share.status}
                                  onChange={(e) =>
                                    handleCandidateStatusChange(
                                      share.candidateId._id,
                                      share._id,
                                      e.target.value
                                    )
                                  }
                                  sx={{
                                    fontSize: 13,
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D5DB" }
                                  }}
                                >
                                  <MenuItem value="Pending">Pending</MenuItem>
                                  <MenuItem value="Reviewed">Reviewed</MenuItem>
                                  <MenuItem value="On Hold">On Hold</MenuItem>
                                  <MenuItem value="Shared">Shared</MenuItem>
                                </Select>
                              </FormControl>

                              <Button
                                onClick={() => handleSaveRemarks(share._id, share.remarks)}
                                variant="contained"
                                size="small"
                                disabled={!editedRows.has(share.candidateId._id)}
                                startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  fontSize: 12,
                                  px: 2,
                                  py: 0.75,
                                  textTransform: "none",
                                  backgroundColor: editedRows.has(share.candidateId._id)
                                    ? "#10B981"
                                    : "#D1D5DB",
                                  "&:hover": {
                                    backgroundColor: editedRows.has(share.candidateId._id)
                                      ? "#059669"
                                      : "#D1D5DB"
                                  }
                                }}
                              >
                                Save
                              </Button>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box className="flex items-center gap-2 text-gray-600">
                              <AccessTimeIcon fontSize="small" />
                              <Typography sx={{ fontSize: 12 }}>
                                {datetimeCoverter(share.sharedAt)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 8, 10]}
              component="div"
              count={sharedCandidates.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserHomePage;
